<template>
  <panel :title="title">
    <div slot="toolbar">
      {{ displayStatus(pack.status) }}
    </div>

    <w-table
      entity-type="payment"
      :hide-add-button="true"
      :hide-search-input="true"
      :actions="actions"
      :columns="columns"
      :data="list"
      :total="count"
      :per-page="-1"
      :force-not-exact="true"
      :foot-data="footData"
      @cancel="cancel"
      @cancelKsat="cancelInKsat"
      @reattach="reattach"
      @view="preview"
      @viewDocument="goToDocument"
      @resend="resend"
    />

    <div slot="footer">
      <div>
        <template v-if="koEnabled">
          <check-permission permissions="PAYMENT.UPDATE" entity-type="payment_pack" :entity-id="packId">
            <w-button
              class="btn-primary float-right ml-2"
              text="Oznacz środki wypłacone"
              :disabled="!isListSent"
              @click="openModal('paid')"
            />
          </check-permission>

          <check-permission permissions="PAYMENT.MDOK" entity-type="payment_pack" :entity-id="packId">
            <w-button
              class="btn-primary float-right ml-2"
              text="Prześlij listę BK do MDOK"
              :disabled="!isReceivedFunds"
              @click="openModal('koPackPreview')"
            />
          </check-permission>

          <check-permission permissions="PAYMENT.UPDATE" entity-type="payment_pack" :entity-id="packId">
            <w-button
              class="btn-primary float-right ml-2"
              text="Oznacz środki KO jako otrzymane"
              :disabled="!isKoSent"
              @click="openModal('receivedFunds')"
            />
          </check-permission>

          <check-permission permissions="PAYMENT.MDOK" entity-type="payment_pack" :entity-id="packId">
            <w-button
              class="btn-primary float-right ml-2"
              text="Prześlij Wniosek KO do MDOK"
              :disabled="!isConfirmed"
              @click="openModal('ko')"
            />
          </check-permission>
        </template>

        <template v-else>
          <check-permission permissions="PAYMENT.MDOK" entity-type="payment_pack" :entity-id="packId">
            <post-form
              class="float-right ml-2"
              button-class="btn-primary"
              text="Podgląd PDF"
              aria-label-text="Podgląd PDF"
              :url="downloadPdfLink"
            />
          </check-permission>

          <check-permission permissions="PAYMENT.MDOK" entity-type="payment_pack" :entity-id="packId">
            <w-button
              class="btn-primary float-right ml-2"
              text="Prześlij do MDOK"
              :disabled="!isConfirmed"
              @click="sendToMdok"
            />
          </check-permission>

          <check-permission v-if="mdokUid" permissions="PAYMENT.MDOK" entity-type="payment_pack" :entity-id="packId">
            <w-button icon="fas fa-link mr-2" class="btn-light mr-3" text="Przejdź do MDOK" @click="goToMdok" />
          </check-permission>
        </template>

        <check-permission
          v-if="canBeConfirmed"
          permissions="PAYMENT.UPDATE"
          entity-type="payment_pack"
          :entity-id="packId"
        >
          <w-button class="btn-light" text="Zatwierdź" :disabled="!canConfirm" @click="confirmPack" />
        </check-permission>

        <check-permission v-else permissions="PAYMENT.UNCONFIRM" entity-type="payment_pack" :entity-id="packId">
          <w-button class="btn-light" text="Odtwierdź" :disabled="!isConfirmed" @click="unconfirmPack" />
        </check-permission>

        <check-permission
          v-if="!ksatDisabled"
          permissions="PAYMENT.UPDATE"
          entity-type="payment_pack"
          :entity-id="packId"
        >
          <w-button
            class="btn-primary float-right ml-2"
            text="Prześlij do KSAT"
            :disabled="!isConfirmed || isSent || sendToKsatDisabled"
            @click="sendToKsat"
          />
        </check-permission>
      </div>
    </div>

    <payment-details-modal
      v-if="modals.preview"
      :show="modals.preview"
      @close="closeModal('preview')"
      @update="updateContractor"
    />

    <ko-proposal-modal v-if="modals.ko" :show="modals.ko" @close="closeModal('ko')" />

    <payment-pack-paid-modal v-if="modals.paid" :show="modals.paid" @close="closeModal('paid')" />

    <payment-pack-received-funds-modal
      v-if="modals.receivedFunds"
      :show="modals.receivedFunds"
      @close="closeModal('receivedFunds')"
    />

    <ko-payment-pack-preview-modal
      v-if="modals.koPackPreview"
      :show="modals.koPackPreview"
      @close="closeModal('koPackPreview')"
    />
  </panel>
</template>

<script>
import { getPath } from '@witkac/utils/getPath';
import { mapState } from 'vuex';

import KoPaymentPackPreviewModal from '@/components/modals/KoPaymentPackPreviewModal';
import KoProposalModal from '@/components/modals/KoProposalModal';
import PaymentDetailsModal from '@/components/modals/PaymentDetailsModal';
import PaymentPackPaidModal from '@/components/modals/PaymentPackPaidModal';
import PaymentPackReceivedFundsModal from '@/components/modals/PaymentPackReceivedFundsModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { Options } from '@/enums/options.enum';
import { PaymentState, PaymentStateTranslations } from '@/enums/paymentState';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { TableName } from '@/enums/tableName';
import { date, dateTime } from '@/filters/date';
import { formatAmount } from '@/filters/formatAmount';
import { displayModalMixin } from '@/mixins/displayModal';
import { koMixin } from '@/mixins/ko';
import { listMixin } from '@/mixins/list';
import { documentResource } from '@/resources/document.resource';
import { paymentPackResource } from '@/resources/paymentPack.resource';
import { config } from '@/utils/config';
import {
  CANCEL_ACTION,
  CANCEL_KSAT_ACTION,
  REATTACH_ACTION,
  RESEND_ACTION,
  VIEW_ACTION,
  VIEW_DOCUMENT_ACTION
} from '@/utils/constants';
import { calculateFootData } from '@/utils/footData';
import { tryParseInt } from '@/utils/number';
import { getPaymentPackDetails } from '@/utils/paymentList';
import { prepareQuery } from '@/utils/prepareQuery';
import { serverError } from '@/utils/serverError';

const messages = {};

// @vuese
// Lista płatności
// @group Procedure
export default {
  components: {
    KoPaymentPackPreviewModal,
    KoProposalModal,
    Panel,
    PaymentDetailsModal,
    PaymentPackPaidModal,
    PaymentPackReceivedFundsModal,
    PostForm,
    WTable
  },

  mixins: [displayModalMixin, koMixin, listMixin('payment', messages)],

  data() {
    return {
      actions: [VIEW_ACTION, VIEW_DOCUMENT_ACTION, CANCEL_ACTION, CANCEL_KSAT_ACTION, REATTACH_ACTION, RESEND_ACTION],
      showKoModal: false
    };
  },

  computed: {
    ...mapState({
      ksatDisabled: state => state.setting.settings[PublicSettings.KSAT_DISABLED]?.value === 'true',
      splitDisabled: state => state.setting.settings[PublicSettings.PAYMENT_NO_SPLIT]?.value === 'true',
      settingColumns: state =>
        state.setting.settings[PublicSettings.PAYMENT_PACK_DETAILS_COLUMNS]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.PAYMENT_PACK_DETAILS_COLUMNS]?.value),
      pack: state => state.paymentPack.list[0],
      isSecretary: state => !!state.auth.nodes?.length,
      list(state) {
        let list = state.payment.list;
        list.forEach(payment => {
          payment.correctKSAT = this.correctKSAT(payment);
          if (payment.state === PaymentState.CANCELED) {
            payment.listClasses = 'table-secondary';
          } else if (!payment.correctKSAT) {
            payment.listClasses = 'table-danger';
          }
        });
        const order = this.$route.query.order;
        if (!order) {
          return list;
        }
        const desc = this.$route.query.desc === 'true' || this.$route.query.desc === true;
        return list.sort((paymentA, paymentB) => {
          const valueA = getPath(paymentA, order);
          const valueB = getPath(paymentB, order);
          if (typeof valueA === 'number') {
            return (valueA - valueB) * (desc ? -1 : 1);
          }
          if (typeof valueA === 'string') {
            return valueA.localeCompare(valueB) * (desc ? -1 : 1);
          }
          return 0;
        });
      }
    }),

    title() {
      return this.$route.meta?.header?.title || 'Płatności';
    },

    defaultColumns() {
      const columns = [
        { name: 'id', displayName: 'ID', sortable: true },
        {
          name: 'numberRaw',
          displayName: 'Numer',
          sortable: true,
          nameTransformer: (value, payment) => payment.number
        },
        {
          name: 'state',
          displayName: 'Status',
          sortable: true,
          nameTransformer: (value, payment) => {
            const state = PaymentStateTranslations[value] || 'Nieznany status';
            if (value === PaymentState.CONFIRMED) {
              return state;
            }
            const noIdsError = !payment.correctKSAT && 'Błąd: Kontrahent wymaga walidacji w KSAT';
            const errorMessages = [payment.error, noIdsError]
              .filter(Boolean)
              .map(error => `<p class="small">${error}</p>`)
              .join('');
            return state + errorMessages;
          }
        },
        { name: 'created', displayName: 'Data utworzenia', sortable: true, nameTransformer: value => dateTime(value) },
        { name: 'date', displayName: 'Termin płatności', sortable: true, nameTransformer: value => date(value) },
        { name: 'data.contractor.lastName', displayName: 'Nazwisko' },
        { name: 'data.contractor.firstName', displayName: 'Imię' },
        {
          name: 'data.contractor.bankAccountNumber',
          displayName: 'Numer konta',
          nameTransformer: value => value?.split(' ').join('&nbsp;')
        },
        { name: 'pack.structure.name', displayName: 'Jednostka', nameTransformer: value => value || 'OW' },
        { name: 'externalId', displayName: 'Identyfikator KSAT', sortable: true },
        {
          cssClass: 'text-right',
          displayName: 'Kwota',
          name: 'amount',
          nameTransformer: formatAmount,
          sortable: true
        }
      ];
      if (!this.splitDisabled) {
        columns.push(
          {
            cssClass: 'text-right',
            displayName: 'Dotacja',
            name: 'grant',
            nameTransformer: formatAmount
          },
          {
            cssClass: 'text-right',
            displayName: 'Wkład własny',
            name: 'contribution',
            nameTransformer: formatAmount
          }
        );
      }
      return columns;
    },

    columns() {
      return this.settingColumns || this.defaultColumns;
    },

    footData() {
      if (this.koConfig.enableKo) {
        return calculateFootData(this.list, this.columns);
      }
    },

    packId() {
      return this.$route.params.packId;
    },

    downloadPdfLink() {
      return `${config.renderURL}/pdf/payment_list/${this.packId}`;
    },

    mdokUid() {
      if (this.isSecretary) {
        return this.pack.systemNumbers?.mdokJoId;
      }
      return this.pack.systemNumbers?.mdokId;
    },

    sendToKsatDisabled() {
      return (
        !this.isConfirmed ||
        this.list.some(payment => payment.state !== PaymentState.CANCELED && payment.state !== PaymentState.CONFIRMED)
      );
    },

    canBeConfirmed() {
      return [PaymentState.UNCONFIRMED, PaymentState.NOT_SENT].includes(this.pack.status);
    },

    isConfirmed() {
      return this.pack.status === PaymentState.CONFIRMED;
    },

    isKoSent() {
      return this.pack.status === PaymentState.KO_SENT;
    },

    isListSent() {
      return this.pack.status === PaymentState.LIST_SENT;
    },

    isReceivedFunds() {
      return this.pack.status === PaymentState.RECEIVED_FUNDS;
    },

    isSent() {
      return !!this.pack.externalId;
    },

    canConfirm() {
      return this.list.every(
        payment =>
          payment.state === PaymentState.CANCELED ||
          payment.state === PaymentState.CONFIRMED ||
          (payment.state === PaymentState.NOT_SENT &&
            payment.data.contractorId &&
            payment.data.contractorAddressId &&
            (!payment.data.contractor.bankAccountNumber || payment.data.contractorBankAccountId))
      );
    }
  },
  methods: {
    // @vuese
    // Przeładowuje paczkę
    reload() {
      return getPaymentPackDetails(this.$route).then(() => this.$router.replace({ query: { reload: Date.now() } }));
    },

    // @vuese
    // Przesyła paczkę do MDOK
    sendToMdok() {
      paymentPackResource
        .sendToMdok(tryParseInt(this.packId))
        .then(() => this.notifySuccess(Successes.PAYMENT_PACK_SENT_TO_MDOK))
        .then(() => this.reload())
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Przesyła paczkę do KSAT
    sendToKsat() {
      paymentPackResource
        .sendToKsat(tryParseInt(this.packId))
        .then(() => this.notifySuccess('Paczka została przesłana do KSAT.'))
        .then(() => this.reload())
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Przechodzi do paczki w MDOK
    goToMdok() {
      documentResource
        .getMdokUrlByUid(tryParseInt(this.mdokUid), undefined, undefined, tryParseInt(this.packId))
        .then(url => window.open(url, '_blank'));
    },

    // @vuese
    // Anulowanie wypłaty
    cancel(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź anulowanie zobowiązania',
        description: `Czy jesteś pewien, że chcesz anulować zobowiązanie numer "${payment.number}" tylko w systemie Witkac?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/cancel', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_CANCELED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_CANCEL))
      });
    },

    // @vuese
    // Anulowanie wypłaty w KSAT
    cancelInKsat(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź anulowanie zobowiązania',
        description: `Czy jesteś pewien, że chcesz anulować zobowiązanie numer "${payment.number}" w systemach Witkac i KSAT?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/cancelInKsat', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_CANCELED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_CANCEL))
      });
    },

    // @vuese
    // Dołączenie zobowiązania do paczki dokumentów
    reattach(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź dołączenie zobowiązania do paczki',
        description: `Czy jesteś pewien, że chcesz dołączyć zobowiązanie numer "${payment.number}" do paczki dokumentów w systemie KSAT?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/reattachInKsat', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_REATTACHED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_REATTACH))
      });
    },

    // @vuese
    // Ponowne przesłanie zobowiązania do KSAT
    resend(payment) {
      this.$store
        .dispatch('payment/resendToKsat', { id: payment.id })
        .then(() => {
          this.notifySuccess(Successes.PAYMENT_RESENT);
          this.updateList(this.$route);
        })
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Ponowne przesłanie zobowiązania do KSAT
    updateContractor(variables) {
      this.$store
        .dispatch('payment/updateContractor', variables)
        .then(() => {
          this.notifySuccess(Successes.PAYMENT_CONTRACTOR_UPDATED);
          this.closeModal('preview');
          this.updateList(this.$route);
        })
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Sprawdza czy podano numery KSAT w danej płatności
    correctKSAT(payment) {
      return (
        this.ksatDisabled ||
        (payment?.data &&
          payment.data.contractorAddressId &&
          payment.data.contractorId &&
          (payment.data.contractorBankAccountId || !payment.data.contractor?.bankAccountNumber))
      );
    },
    // @vuese
    // Zatwierdzenie paczki
    confirmPack() {
      this.$store
        .dispatch('paymentPack/confirm', tryParseInt(this.packId))
        .then(() => this.notifySuccess(this.koConfig.confirmSuccessText || Successes.PAYMENT_PACK_CONFIRMED))
        .then(() => getPaymentPackDetails(this.$route))
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Odtwierdzenie paczki
    unconfirmPack() {
      this.$store
        .dispatch('paymentPack/unconfirm', tryParseInt(this.packId))
        .then(() => this.notifySuccess(this.koConfig.unconfirmSuccessText || Successes.PAYMENT_PACK_UNCONFIRMED))
        .then(() => getPaymentPackDetails(this.$route))
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Otwiera modal podglądu
    preview(payment) {
      Promise.all([
        this.$store.dispatch('documentIndex/get', payment.documentId),
        this.$store.dispatch('optionValue/getByCode', {
          code: Options.DICTIONARY,
          entityType: TableName.status,
          entityId: payment.document.statusId.toString()
        })
      ])
        .then(() => {
          this.$store.commit('payment/setEntity', payment);
          this.openModal('preview');
        })
        .catch(err => this.notifyError(Errors.DOCUMENT_CANNOT_GET));
    },

    // @vuese
    // Przejście do dokumentu
    goToDocument(payment) {
      this.$router.push({
        name: 'documentView',
        params: { documentId: payment.documentId, procedureId: payment.document.procedureId }
      });
    },

    // @vuese
    // Filtry listy
    getFilters(route) {
      return {
        ...prepareQuery(route),
        count: -1,
        start: 0,
        packId: tryParseInt(route.params.packId),
        structureIds: this.$store.state.auth.nodes.map(node => node.id)
      };
    },

    displayStatus(status) {
      return PaymentStateTranslations[status];
    }
  }
};
</script>
